import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, socket } from '../utils/api';
import { cn } from '../utils/cn';
import { Link } from 'react-router-dom';
import {
  Search,
  TrendingUp,
  TrendingDown,
  Activity,
  Zap,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Star,
  Volume2,
  BarChart2,
  Clock,
  RefreshCw,
  ChevronDown,
  Eye,
  Bell,
  Flame,
  Target,
  AlertTriangle,
  Sparkles,
  Layers,
  DollarSign,
} from 'lucide-react';

interface ScannerAsset {
  symbol: string;
  pair: string;
  price: number;
  change1h: number;
  change24h: number;
  change7d: number;
  volume24h: number;
  volumeChange: number;
  high24h: number;
  low24h: number;
  marketCap?: number;
  rsi?: number;
  macdSignal?: 'bullish' | 'bearish' | 'neutral';
  volatility?: number;
  liquidity?: number;
  signals: ScannerSignal[];
}

interface ScannerSignal {
  type: 'volume_spike' | 'price_breakout' | 'rsi_oversold' | 'rsi_overbought' | 'macd_cross' | 'golden_cross' | 'death_cross' | 'whale_alert';
  strength: 'weak' | 'moderate' | 'strong';
  timestamp: number;
  description: string;
}

interface ScannerPreset {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  filters: ScannerFilters;
  color: string;
}

interface ScannerFilters {
  minChange24h?: number;
  maxChange24h?: number;
  minVolume24h?: number;
  minVolumeChange?: number;
  rsiMin?: number;
  rsiMax?: number;
  macdSignal?: 'bullish' | 'bearish';
  hasSignal?: ScannerSignal['type'];
}

const SCANNER_PRESETS: ScannerPreset[] = [
  {
    id: 'gainers',
    name: 'Top Gainers',
    description: 'Assets with highest 24h gains',
    icon: TrendingUp,
    filters: { minChange24h: 3 },
    color: '#00D4AA',
  },
  {
    id: 'losers',
    name: 'Top Losers',
    description: 'Assets with largest 24h drops',
    icon: TrendingDown,
    filters: { maxChange24h: -3 },
    color: '#FF6B6B',
  },
  {
    id: 'volume-spike',
    name: 'Volume Spike',
    description: 'Unusual volume activity',
    icon: Volume2,
    filters: { minVolumeChange: 50 },
    color: '#8B5CF6',
  },
  {
    id: 'oversold',
    name: 'RSI Oversold',
    description: 'Potential bounce candidates',
    icon: Target,
    filters: { rsiMax: 30 },
    color: '#F59E0B',
  },
  {
    id: 'overbought',
    name: 'RSI Overbought',
    description: 'Potential reversal candidates',
    icon: AlertTriangle,
    filters: { rsiMin: 70 },
    color: '#EF4444',
  },
  {
    id: 'breakout',
    name: 'Breakouts',
    description: 'Price breaking key levels',
    icon: Zap,
    filters: { hasSignal: 'price_breakout' },
    color: '#00D4AA',
  },
  {
    id: 'macd-bullish',
    name: 'MACD Bullish',
    description: 'Bullish MACD crossover',
    icon: Sparkles,
    filters: { macdSignal: 'bullish' },
    color: '#10B981',
  },
  {
    id: 'whale-activity',
    name: 'Whale Activity',
    description: 'Large order flow detected',
    icon: Flame,
    filters: { hasSignal: 'whale_alert' },
    color: '#3B82F6',
  },
];

// Generate mock scanner data (in production, this would come from API)
const generateMockData = (): ScannerAsset[] => {
  const pairs = [
    'BTC-USDT', 'ETH-USDT', 'SOL-USDT', 'BNB-USDT', 'XRP-USDT',
    'DOGE-USDT', 'ADA-USDT', 'AVAX-USDT', 'LINK-USDT', 'DOT-USDT',
    'MATIC-USDT', 'UNI-USDT', 'ATOM-USDT', 'LTC-USDT', 'ETC-USDT',
    'FIL-USDT', 'NEAR-USDT', 'APT-USDT', 'ARB-USDT', 'OP-USDT',
    'INJ-USDT', 'TIA-USDT', 'SEI-USDT', 'SUI-USDT', 'PEPE-USDT',
    'WIF-USDT', 'JUP-USDT', 'BONK-USDT', 'RENDER-USDT', 'FET-USDT',
  ];

  return pairs.map(pair => {
    const symbol = pair.split('-')[0];
    const basePrice = symbol === 'BTC' ? 97000 : symbol === 'ETH' ? 3400 : Math.random() * 100;
    const change24h = (Math.random() - 0.5) * 20;
    const volumeChange = (Math.random() - 0.3) * 100;
    const rsi = Math.random() * 100;

    const signals: ScannerSignal[] = [];
    
    if (volumeChange > 50) {
      signals.push({
        type: 'volume_spike',
        strength: volumeChange > 100 ? 'strong' : 'moderate',
        timestamp: Date.now(),
        description: `Volume up ${volumeChange.toFixed(0)}% in 24h`,
      });
    }
    
    if (rsi < 30) {
      signals.push({
        type: 'rsi_oversold',
        strength: rsi < 20 ? 'strong' : 'moderate',
        timestamp: Date.now(),
        description: `RSI at ${rsi.toFixed(1)} - Oversold territory`,
      });
    }
    
    if (rsi > 70) {
      signals.push({
        type: 'rsi_overbought',
        strength: rsi > 80 ? 'strong' : 'moderate',
        timestamp: Date.now(),
        description: `RSI at ${rsi.toFixed(1)} - Overbought territory`,
      });
    }

    if (Math.random() > 0.85) {
      signals.push({
        type: 'whale_alert',
        strength: 'strong',
        timestamp: Date.now(),
        description: 'Large buy order detected on-chain',
      });
    }

    if (change24h > 8) {
      signals.push({
        type: 'price_breakout',
        strength: change24h > 15 ? 'strong' : 'moderate',
        timestamp: Date.now(),
        description: `Breaking out - up ${change24h.toFixed(1)}%`,
      });
    }

    return {
      symbol,
      pair,
      price: basePrice * (1 + (Math.random() - 0.5) * 0.02),
      change1h: (Math.random() - 0.5) * 5,
      change24h,
      change7d: (Math.random() - 0.5) * 30,
      volume24h: Math.random() * 1000000000,
      volumeChange,
      high24h: basePrice * 1.05,
      low24h: basePrice * 0.95,
      marketCap: basePrice * 1000000000 * (Math.random() + 0.1),
      rsi,
      macdSignal: Math.random() > 0.5 ? 'bullish' : Math.random() > 0.5 ? 'bearish' : 'neutral',
      volatility: Math.random() * 100,
      liquidity: Math.random() * 1000000000,
      signals,
    };
  });
};

export default function Scanner() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activePreset, setActivePreset] = useState<string>('gainers');
  const [sortBy, setSortBy] = useState<'change24h' | 'volume24h' | 'volumeChange' | 'rsi'>('change24h');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [favorites, setFavorites] = useState<string[]>(['BTC-USDT', 'ETH-USDT', 'SOL-USDT']);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  
  // Fetch real scanner data from API
  const { data: scannerData, isLoading } = useQuery({
    queryKey: ['scanner-data'],
    queryFn: async () => {
      try {
        const response = await api.get('/market/scanner');
        return response.data;
      } catch {
        return [];
      }
    },
    refetchInterval: 10000,
  });

  // Convert API data to scanner assets format
  const assets = useMemo(() => {
    if (!scannerData || !Array.isArray(scannerData)) return [];
    
    return scannerData.map((item: any) => {
      const symbol = item.symbol?.replace('/USDT', '') || item.name || '';
      return {
        symbol,
        pair: `${symbol}-USDT`,
        price: item.price || 0,
        change1h: item.change1h || item.change24h / 24 || 0,
        change24h: item.change24h || 0,
        change7d: item.change7d || 0,
        volume24h: item.volume || 0,
        volumeChange: item.volumeChange || 0,
        high24h: item.price * 1.05 || 0,
        low24h: item.price * 0.95 || 0,
        marketCap: item.marketCap || 0,
        rsi: item.rsi || 50,
        macdSignal: item.signals?.includes('bullish') ? 'bullish' as const : 
                    item.signals?.includes('bearish') ? 'bearish' as const : 'neutral' as const,
        volatility: Math.abs(item.change24h) || 0,
        liquidity: item.volume || 0,
        signals: (item.signals || []).map((s: string) => ({
          type: s === 'bullish' || s === 'breakout' ? 'price_breakout' :
                s === 'high_volume' ? 'volume_spike' :
                s === 'oversold' ? 'rsi_oversold' :
                s === 'overbought' ? 'rsi_overbought' : 'volume_spike',
          strength: 'moderate' as const,
          timestamp: Date.now(),
          description: s,
        })),
      } as ScannerAsset;
    });
  }, [scannerData]);

  // Filter and sort assets
  const filteredAssets = useMemo(() => {
    const preset = SCANNER_PRESETS.find(p => p.id === activePreset);
    const filters = preset?.filters || {};

    let result = assets.filter(asset => {
      // Search filter
      if (searchQuery && !asset.symbol.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !asset.pair.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Favorites filter
      if (showOnlyFavorites && !favorites.includes(asset.pair)) {
        return false;
      }

      // Preset filters
      if (filters.minChange24h !== undefined && asset.change24h < filters.minChange24h) return false;
      if (filters.maxChange24h !== undefined && asset.change24h > filters.maxChange24h) return false;
      if (filters.minVolume24h !== undefined && asset.volume24h < filters.minVolume24h) return false;
      if (filters.minVolumeChange !== undefined && asset.volumeChange < filters.minVolumeChange) return false;
      if (filters.rsiMin !== undefined && (asset.rsi === undefined || asset.rsi < filters.rsiMin)) return false;
      if (filters.rsiMax !== undefined && (asset.rsi === undefined || asset.rsi > filters.rsiMax)) return false;
      if (filters.macdSignal !== undefined && asset.macdSignal !== filters.macdSignal) return false;
      if (filters.hasSignal !== undefined && !asset.signals.some(s => s.type === filters.hasSignal)) return false;

      return true;
    });

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortBy] || 0;
      let bVal = b[sortBy] || 0;
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });

    return result;
  }, [assets, searchQuery, activePreset, sortBy, sortOrder, showOnlyFavorites, favorites]);

  // Format helpers
  const formatPrice = (price: number) => {
    if (price >= 10000) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 1) return price.toFixed(4);
    return price.toFixed(8);
  };

  const formatVolume = (vol: number) => {
    if (vol >= 1e9) return '$' + (vol / 1e9).toFixed(2) + 'B';
    if (vol >= 1e6) return '$' + (vol / 1e6).toFixed(2) + 'M';
    if (vol >= 1e3) return '$' + (vol / 1e3).toFixed(2) + 'K';
    return '$' + vol.toFixed(2);
  };

  const formatChange = (change: number) => {
    const sign = change >= 0 ? '+' : '';
    return sign + change.toFixed(2) + '%';
  };

  const toggleFavorite = (pair: string) => {
    setFavorites(prev => 
      prev.includes(pair) ? prev.filter(p => p !== pair) : [...prev, pair]
    );
  };

  const SignalBadge = ({ signal }: { signal: ScannerSignal }) => {
    const colors: Record<ScannerSignal['type'], string> = {
      volume_spike: '#8B5CF6',
      price_breakout: '#00D4AA',
      rsi_oversold: '#F59E0B',
      rsi_overbought: '#EF4444',
      macd_cross: '#3B82F6',
      golden_cross: '#00D4AA',
      death_cross: '#EF4444',
      whale_alert: '#3B82F6',
    };

    const labels: Record<ScannerSignal['type'], string> = {
      volume_spike: 'VOL',
      price_breakout: 'BREAK',
      rsi_oversold: 'OVERSOLD',
      rsi_overbought: 'OVERBOUGHT',
      macd_cross: 'MACD',
      golden_cross: 'GOLDEN',
      death_cross: 'DEATH',
      whale_alert: 'WHALE',
    };

    return (
      <span
        className="px-1.5 py-0.5 rounded text-[10px] font-bold"
        style={{ 
          backgroundColor: `${colors[signal.type]}20`,
          color: colors[signal.type],
        }}
        title={signal.description}
      >
        {labels[signal.type]}
      </span>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-[#080b0f]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/[0.06] bg-[#0d1117]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Market Scanner</h1>
              <p className="text-sm text-[#8b949e]">Real-time market analysis & signals</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8b949e]" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="input pl-10 w-64"
              />
            </div>

            {/* Favorites toggle */}
            <button
              onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all",
                showOnlyFavorites
                  ? "bg-primary/10 text-primary"
                  : "bg-white/[0.04] text-[#8b949e] hover:text-white"
              )}
            >
              <Star className={cn("h-4 w-4", showOnlyFavorites && "fill-current")} />
              Watchlist
            </button>

            {/* Refresh indicator */}
            <div className="flex items-center gap-2 text-xs text-[#8b949e]">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Live
            </div>
          </div>
        </div>

        {/* Preset Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {SCANNER_PRESETS.map(preset => {
            const Icon = preset.icon;
            const count = assets.filter(a => {
              const f = preset.filters;
              if (f.minChange24h !== undefined && a.change24h < f.minChange24h) return false;
              if (f.maxChange24h !== undefined && a.change24h > f.maxChange24h) return false;
              if (f.minVolumeChange !== undefined && a.volumeChange < f.minVolumeChange) return false;
              if (f.rsiMax !== undefined && (a.rsi === undefined || a.rsi > f.rsiMax)) return false;
              if (f.rsiMin !== undefined && (a.rsi === undefined || a.rsi < f.rsiMin)) return false;
              if (f.macdSignal !== undefined && a.macdSignal !== f.macdSignal) return false;
              if (f.hasSignal !== undefined && !a.signals.some(s => s.type === f.hasSignal)) return false;
              return true;
            }).length;

            return (
              <button
                key={preset.id}
                onClick={() => setActivePreset(preset.id)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all",
                  activePreset === preset.id
                    ? "text-white"
                    : "text-[#8b949e] hover:text-white hover:bg-white/[0.04]"
                )}
                style={{
                  backgroundColor: activePreset === preset.id ? `${preset.color}15` : undefined,
                  borderColor: activePreset === preset.id ? preset.color : undefined,
                  color: activePreset === preset.id ? preset.color : undefined,
                }}
              >
                <Icon className="h-4 w-4" />
                {preset.name}
                <span className={cn(
                  "px-1.5 py-0.5 rounded text-[10px] font-bold",
                  activePreset === preset.id ? "bg-white/20" : "bg-white/[0.06]"
                )}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-[#0d1117] z-10">
            <tr className="text-xs text-[#8b949e] border-b border-white/[0.06]">
              <th className="px-4 py-3 text-left font-semibold w-8"></th>
              <th className="px-4 py-3 text-left font-semibold">Asset</th>
              <th className="px-4 py-3 text-right font-semibold">Price</th>
              <th 
                className="px-4 py-3 text-right font-semibold cursor-pointer hover:text-white transition-colors"
                onClick={() => {
                  if (sortBy === 'change24h') setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                  else { setSortBy('change24h'); setSortOrder('desc'); }
                }}
              >
                <div className="flex items-center justify-end gap-1">
                  24h %
                  {sortBy === 'change24h' && (
                    <ChevronDown className={cn("h-3 w-3", sortOrder === 'asc' && "rotate-180")} />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-right font-semibold">1h %</th>
              <th className="px-4 py-3 text-right font-semibold">7d %</th>
              <th 
                className="px-4 py-3 text-right font-semibold cursor-pointer hover:text-white transition-colors"
                onClick={() => {
                  if (sortBy === 'volume24h') setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                  else { setSortBy('volume24h'); setSortOrder('desc'); }
                }}
              >
                <div className="flex items-center justify-end gap-1">
                  Volume
                  {sortBy === 'volume24h' && (
                    <ChevronDown className={cn("h-3 w-3", sortOrder === 'asc' && "rotate-180")} />
                  )}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-right font-semibold cursor-pointer hover:text-white transition-colors"
                onClick={() => {
                  if (sortBy === 'volumeChange') setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                  else { setSortBy('volumeChange'); setSortOrder('desc'); }
                }}
              >
                <div className="flex items-center justify-end gap-1">
                  Vol Δ
                  {sortBy === 'volumeChange' && (
                    <ChevronDown className={cn("h-3 w-3", sortOrder === 'asc' && "rotate-180")} />
                  )}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-right font-semibold cursor-pointer hover:text-white transition-colors"
                onClick={() => {
                  if (sortBy === 'rsi') setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                  else { setSortBy('rsi'); setSortOrder('desc'); }
                }}
              >
                <div className="flex items-center justify-end gap-1">
                  RSI
                  {sortBy === 'rsi' && (
                    <ChevronDown className={cn("h-3 w-3", sortOrder === 'asc' && "rotate-180")} />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left font-semibold">Signals</th>
              <th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAssets.map((asset, idx) => (
              <tr 
                key={asset.pair}
                className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors"
              >
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleFavorite(asset.pair)}
                    className="text-[#8b949e] hover:text-primary transition-colors"
                  >
                    <Star className={cn(
                      "h-4 w-4",
                      favorites.includes(asset.pair) && "fill-primary text-primary"
                    )} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <Link 
                    to={`/trade?pair=${asset.pair}`}
                    className="flex items-center gap-3 hover:text-primary transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-white">{asset.symbol.slice(0, 2)}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-white">{asset.symbol}</div>
                      <div className="text-xs text-[#8b949e]">{asset.pair}</div>
                    </div>
                  </Link>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono text-white font-medium">${formatPrice(asset.price)}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={cn(
                    "font-mono font-medium",
                    asset.change24h >= 0 ? "text-[#00D4AA]" : "text-[#FF6B6B]"
                  )}>
                    {formatChange(asset.change24h)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={cn(
                    "font-mono text-sm",
                    asset.change1h >= 0 ? "text-[#00D4AA]" : "text-[#FF6B6B]"
                  )}>
                    {formatChange(asset.change1h)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={cn(
                    "font-mono text-sm",
                    asset.change7d >= 0 ? "text-[#00D4AA]" : "text-[#FF6B6B]"
                  )}>
                    {formatChange(asset.change7d)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono text-white text-sm">{formatVolume(asset.volume24h)}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={cn(
                    "font-mono text-sm",
                    asset.volumeChange >= 0 ? "text-[#8B5CF6]" : "text-[#8b949e]"
                  )}>
                    {asset.volumeChange >= 0 ? '+' : ''}{asset.volumeChange.toFixed(0)}%
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {asset.rsi !== undefined && (
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-white/[0.1] overflow-hidden">
                        <div 
                          className={cn(
                            "h-full rounded-full transition-all",
                            asset.rsi < 30 ? "bg-[#F59E0B]" : 
                            asset.rsi > 70 ? "bg-[#EF4444]" : 
                            "bg-[#00D4AA]"
                          )}
                          style={{ width: `${asset.rsi}%` }}
                        />
                      </div>
                      <span className={cn(
                        "font-mono text-sm w-8 text-right",
                        asset.rsi < 30 ? "text-[#F59E0B]" : 
                        asset.rsi > 70 ? "text-[#EF4444]" : 
                        "text-[#8b949e]"
                      )}>
                        {asset.rsi.toFixed(0)}
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    {asset.signals.slice(0, 3).map((signal, i) => (
                      <SignalBadge key={i} signal={signal} />
                    ))}
                    {asset.signals.length > 3 && (
                      <span className="text-[10px] text-[#8b949e]">+{asset.signals.length - 3}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Link
                      to={`/trade?pair=${asset.pair}`}
                      className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      title="Trade"
                    >
                      <BarChart2 className="h-4 w-4" />
                    </Link>
                    <button
                      className="p-1.5 rounded-lg bg-white/[0.04] text-[#8b949e] hover:text-white hover:bg-white/[0.08] transition-colors"
                      title="Set Alert"
                    >
                      <Bell className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredAssets.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-[#8b949e]">
            <Search className="h-12 w-12 mb-4 opacity-30" />
            <p className="text-lg font-medium">No assets match your criteria</p>
            <p className="text-sm">Try adjusting your filters or search query</p>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="px-6 py-3 border-t border-white/[0.06] bg-[#0d1117] flex items-center justify-between">
        <div className="flex items-center gap-6 text-xs">
          <span className="text-[#8b949e]">
            Showing <span className="text-white font-medium">{filteredAssets.length}</span> of{' '}
            <span className="text-white font-medium">{assets.length}</span> assets
          </span>
          <span className="text-[#8b949e]">
            Gainers: <span className="text-[#00D4AA] font-medium">{assets.filter(a => a.change24h > 0).length}</span>
          </span>
          <span className="text-[#8b949e]">
            Losers: <span className="text-[#FF6B6B] font-medium">{assets.filter(a => a.change24h < 0).length}</span>
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#8b949e]">
          <Clock className="h-3 w-3" />
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
}
